// Carrossel de flys de ofertas no topo da vitrine publica (Onda Catalogo Fase 1b.4).
// Mostra o fly (arte 1080x1080) de TODAS as promocoes elegiveis, em rotacao —
// o visitante ve mais produtos. Ordem embaralhada a cada visita; toque abre em
// tela cheia. Sem promocao elegivel, o carrossel some sozinho.
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

type Fly = { promocao_id: number; grupo_nome: string; url: string };

function embaralhar<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

const setaStyle = (lado: 'left' | 'right'): CSSProperties => ({
  position: 'absolute',
  top: '50%',
  left: lado === 'left' ? 6 : undefined,
  right: lado === 'right' ? 6 : undefined,
  transform: 'translateY(-50%)',
  width: 34,
  height: 34,
  borderRadius: 999,
  border: 0,
  background: 'rgba(0,0,0,0.45)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
});

export function FlyDoDiaBanner() {
  const [flys, setFlys] = useState<Fly[]>([]);
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const touchX = useRef<number | null>(null);

  useEffect(() => {
    fetch('/api/public/catalogo/flys')
      .then((r) => r.json())
      .then((lista: Fly[]) => setFlys(Array.isArray(lista) ? embaralhar(lista) : []))
      .catch(() => setFlys([]));
  }, []);

  // Rotacao automatica a cada 6s — pausa quando o lightbox esta aberto.
  useEffect(() => {
    if (flys.length < 2 || zoom) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % flys.length), 6000);
    return () => clearInterval(t);
  }, [flys.length, zoom]);

  // Teclado no lightbox: Esc fecha, setas navegam.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoom(false);
      else if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % flys.length);
      else if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + flys.length) % flys.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, flys.length]);

  if (flys.length === 0) return null;

  const ir = (delta: number) => setIdx((i) => (i + delta + flys.length) % flys.length);
  const varios = flys.length > 1;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1rem' }}>
        {/* Palco quadrado — flys empilhados, so o atual visivel (cross-fade). */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 460,
            aspectRatio: '1 / 1',
            borderRadius: 12,
            background: '#eee',
            overflow: 'hidden',
            boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
          }}
          onTouchStart={(e) => { touchX.current = e.touches[0]!.clientX; }}
          onTouchEnd={(e) => {
            if (touchX.current == null) return;
            const dx = e.changedTouches[0]!.clientX - touchX.current;
            touchX.current = null;
            if (varios && Math.abs(dx) > 40) ir(dx < 0 ? 1 : -1);
          }}
        >
          {flys.map((f, i) => (
            <img
              key={f.promocao_id}
              src={f.url}
              alt={`Ofertas — ${f.grupo_nome}`}
              onClick={() => i === idx && setZoom(true)}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: i === idx ? 1 : 0,
                transition: 'opacity 0.4s ease',
                pointerEvents: i === idx ? 'auto' : 'none',
                cursor: 'zoom-in',
              }}
            />
          ))}
          {varios && (
            <>
              <button type="button" onClick={() => ir(-1)} aria-label="Anterior" style={setaStyle('left')}>
                <ChevronLeft size={20} />
              </button>
              <button type="button" onClick={() => ir(1)} aria-label="Proximo" style={setaStyle('right')}>
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </div>

        {/* Indicadores (dots) */}
        {varios && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {flys.map((f, i) => (
              <button
                key={f.promocao_id}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Oferta ${i + 1}`}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  border: 0,
                  cursor: 'pointer',
                  padding: 0,
                  background: i === idx ? '#b45309' : '#d1d5db',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox — fly em tela cheia. Fecha por clique no fundo, X ou Esc. */}
      {zoom && (
        <div
          onClick={() => setZoom(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <img
            src={flys[idx]!.url}
            alt={`Ofertas — ${flys[idx]!.grupo_nome}`}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }}
          />
          {varios && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); ir(-1); }}
                aria-label="Anterior"
                style={{ ...setaStyle('left'), width: 44, height: 44, left: 12, background: 'rgba(255,255,255,0.15)' }}
              >
                <ChevronLeft size={26} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); ir(1); }}
                aria-label="Proximo"
                style={{ ...setaStyle('right'), width: 44, height: 44, right: 12, background: 'rgba(255,255,255,0.15)' }}
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setZoom(false)}
            aria-label="Fechar"
            style={{
              position: 'fixed',
              top: 14,
              right: 14,
              width: 40,
              height: 40,
              borderRadius: 999,
              border: 0,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={22} />
          </button>
        </div>
      )}
    </>
  );
}
